import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { usePlans } from "@/hooks/usePlans";
import {
  stripeCheckoutSchema,
  type StripeCheckoutFormData,
} from "@/lib/validations";
import { Plan } from "@/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { CreditCard, Lock } from "lucide-react";
import { useForm } from "react-hook-form";

interface StripeCheckoutDialogProps {
  plan: Plan;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function StripeCheckoutDialog({
  plan,
  open,
  onOpenChange,
}: StripeCheckoutDialogProps) {
  const { toast } = useToast();
  const { subscribeToPlan, isSubscribing } = usePlans();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<StripeCheckoutFormData>({
    resolver: zodResolver(stripeCheckoutSchema),
  });

  const onSubmit = async (_: StripeCheckoutFormData) => {
    const result = await subscribeToPlan(plan.id);

    if (result.success) {
      toast({
        title: "Payment successful!",
        description: `You have successfully upgraded to the ${plan.name} plan.`,
      });
      onOpenChange(false);
      reset();
    } else {
      toast({
        title: "Payment failed",
        description: result.error,
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-slate-900 border-cyan-500/30">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-cyan-400 ">
            <Lock className="h-5 w-5" />
            Secure Checkout
          </DialogTitle>
          <DialogDescription className="text-slate-400 ">
            Complete your upgrade to the {plan.name} plan
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Order Summary */}
          <Card className="holo-card">
            <CardHeader>
              <CardTitle className="text-lg text-cyan-400 ">
                Order Summary
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex justify-between items-start">
                  <div>
                    <h4 className="font-medium text-cyan-100 ">
                      {plan.name} Plan
                    </h4>
                    <p className="text-sm text-slate-400 ">
                      {plan.description}
                    </p>
                  </div>
                  <Badge className="text-cyan-400 bg-cyan-400/10 border-cyan-400/30 ">
                    Monthly
                  </Badge>
                </div>

                <div className="border-t border-slate-700/50 pt-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400 ">Subtotal</span>
                    <span className="text-cyan-100 ">
                      ${plan.costPerUserCents / 100}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400 ">Tax</span>
                    <span className="text-cyan-100 ">
                      ${((plan.costPerUserCents * 0.1) / 100).toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between font-medium text-lg border-t border-slate-700/50 pt-2 mt-2">
                    <span className="text-cyan-100 ">Total</span>
                    <span className="text-cyan-400 ">
                      ${((plan.costPerUserCents * 1.1) / 100).toFixed(2)}
                    </span>
                  </div>
                </div>

                <div className="text-xs text-slate-400 ">
                  You will be charged $
                  {((plan.costPerUserCents * 1.1) / 100).toFixed(2)} monthly.
                  You can cancel anytime.
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Payment Form */}
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <Card className="holo-card">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2 text-cyan-400 ">
                  <CreditCard className="h-5 w-5" />
                  Payment Details
                </CardTitle>
                <CardDescription className="text-slate-400 ">
                  Your payment information is secure and encrypted
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="nameOnCard" className="text-cyan-300 ">
                    Name on Card
                  </Label>
                  <Input
                    id="nameOnCard"
                    {...register("nameOnCard")}
                    placeholder="John Doe"
                    className="bg-slate-800/50 border-cyan-500/30 text-cyan-100  focus:border-cyan-400"
                  />
                  {errors.nameOnCard && (
                    <p className="text-red-400 text-xs ">
                      {errors.nameOnCard.message}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="cardNumber" className="text-cyan-300 ">
                    Card Number
                  </Label>
                  <Input
                    id="cardNumber"
                    {...register("cardNumber")}
                    placeholder="1234 5678 9012 3456"
                    className="bg-slate-800/50 border-cyan-500/30 text-cyan-100  focus:border-cyan-400"
                  />
                  {errors.cardNumber && (
                    <p className="text-red-400 text-xs ">
                      {errors.cardNumber.message}
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="expiryDate" className="text-cyan-300 ">
                      Expiry Date
                    </Label>
                    <Input
                      id="expiryDate"
                      {...register("expiryDate")}
                      placeholder="MM/YY"
                      className="bg-slate-800/50 border-cyan-500/30 text-cyan-100  focus:border-cyan-400"
                    />
                    {errors.expiryDate && (
                      <p className="text-red-400 text-xs ">
                        {errors.expiryDate.message}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="cvv" className="text-cyan-300 ">
                      CVV
                    </Label>
                    <Input
                      id="cvv"
                      {...register("cvv")}
                      placeholder="123"
                      className="bg-slate-800/50 border-cyan-500/30 text-cyan-100  focus:border-cyan-400"
                    />
                    {errors.cvv && (
                      <p className="text-red-400 text-xs ">
                        {errors.cvv.message}
                      </p>
                    )}
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full cyber-button "
                  disabled={isSubscribing}
                >
                  {isSubscribing
                    ? "PROCESSING..."
                    : `PAY $${((plan.costPerUserCents * 1.1) / 100).toFixed(
                        2,
                      )}`}
                </Button>

                <div className="text-xs text-slate-400 text-center ">
                  Payments are processed securely by Stripe
                </div>
              </CardContent>
            </Card>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
